import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import {
  Plus, Search, Upload, Eye, Check, X, ExternalLink, Trash2, Send, Edit2,
  ChevronDown, ChevronLeft, ChevronRight, Image, FileText, Youtube, Calendar, CalendarDays, User, Clock, Filter, RotateCcw,
  BarChart3, CalendarCheck, SearchX, AlertCircle, FileX, Bell, Settings, PinOff, Link2, Copy
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  completed: 'bg-green-100 text-green-700',
};

const STATUS_LABELS = {
  draft: 'Draft',
  completed: 'Done',
};

const SpecialEvents = ({ mode = 'scheduling' }) => {
  // mode: 'scheduling' → channel accordion + notification slots (no stat cards)
  //       'library'    → stat cards + flat card grid (no accordion / no slots)
  const isSchedulingMode = mode === 'scheduling';
  const isLibraryMode = mode === 'library';
  const user = authService.getCurrentUser();
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [formData, setFormData] = useState({
    title: '', description: '', youtube_link: '', youtube_channel_id: '', event_date: '', event_time: '', teacher_id: '',
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [pptFile, setPptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [activeCard, setActiveCard] = useState('');
  // New: channel accordion + slot assignments + notification config
  const [collapsedChannels, setCollapsedChannels] = useState({});  // {channelId: true}
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [slotAssignments, setSlotAssignments] = useState([]);      // rows from /slot-assignments
  const [slotDate, setSlotDate] = useState(new Date().toISOString().split('T')[0]);
  const [configChannel, setConfigChannel] = useState(null);        // channel doc being edited in Notification Config modal
  const [assignSlotContext, setAssignSlotContext] = useState(null);// {channel, slotIndex} when picking which event to pin

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.getSpecialEventDashboard();
      setDashboardStats(res.data);
    } catch (err) {
      console.error('Failed to load dashboard stats');
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCard) {
        params.append('dashboard_filter', activeCard);
      } else {
        if (statusFilter) params.append('status', statusFilter);
        if (selectedDate) params.append('date', selectedDate);
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
      }
      params.append('limit', '100');
      const res = await api.getSpecialEvents(params.toString());
      setEvents(res.data.events);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, selectedDate, dateFrom, dateTo, activeCard]);

  const fetchTeachers = useCallback(async () => {
    try {
      const res = await api.getSpecialEventTeachers();
      setTeachers(res.data);
    } catch (err) {
      console.error('Failed to load teachers');
    }
  }, []);

  const fetchChannels = useCallback(async () => {
    try {
      const res = await api.getSpecialEventChannels();
      setChannels(res.data);
    } catch (err) {
      console.error('Failed to load channels');
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchTeachers(); }, [fetchTeachers]);
  useEffect(() => { fetchChannels(); }, [fetchChannels]);
  useEffect(() => { if (isLibraryMode) fetchDashboard(); }, [isLibraryMode, fetchDashboard]);

  const fetchSlotAssignments = useCallback(async () => {
    if (!slotDate) return;
    try {
      const res = await api.getSlotAssignments(slotDate);
      setSlotAssignments(res.data || []);
    } catch (err) {
      // silent — slots are optional
    }
  }, [slotDate]);
  useEffect(() => { fetchSlotAssignments(); }, [fetchSlotAssignments]);

  // Group filtered events by channel. Events without a channel go to "Unassigned Channel"
  const groupedEvents = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const key = ev.youtube_channel_id || '__none__';
      const name = ev.youtube_channel_name || 'No Channel';
      if (!map.has(key)) map.set(key, { channelId: key, channelName: name, events: [] });
      map.get(key).events.push(ev);
    }
    // Always include all active channels — even with 0 events — so notification slots are visible
    for (const ch of channels) {
      if (!map.has(ch.id)) {
        map.set(ch.id, { channelId: ch.id, channelName: ch.name, events: [] });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.channelName.localeCompare(b.channelName));
  }, [events, channels]);

  const channelById = useMemo(() => {
    const m = new Map();
    for (const ch of channels) m.set(ch.id, ch);
    return m;
  }, [channels]);

  const slotsFor = (channelId) => slotAssignments.filter(s => s.channel_id === channelId);

  // Late warning: an event scheduled today whose event_time is within lead_hours of now and still lacks materials/completion
  const nowMinutesUTC = () => { const d = new Date(); return d.getUTCHours() * 60 + d.getUTCMinutes(); };
  const parseHM = (t) => { if (!t) return null; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const isEventLate = (ev) => {
    if (ev.status === 'completed') return false;
    const ch = channelById.get(ev.youtube_channel_id);
    const lead = (ch?.notification_lead_hours ?? 6) * 60;
    if (!ev.event_date || !ev.event_time) return false;
    const today = new Date().toISOString().split('T')[0];
    if (ev.event_date > today) return false;
    if (ev.event_date < today) return true;
    const evMin = parseHM(ev.event_time);
    if (evMin == null) return false;
    return (evMin - nowMinutesUTC()) <= lead;
  };

  const isSlotLate = (channel, slotIndex, date) => {
    const slot = channel?.notification_slots?.[slotIndex];
    if (!slot?.time) return false;
    if (date !== new Date().toISOString().split('T')[0]) return date < new Date().toISOString().split('T')[0];
    const lead = (channel.notification_lead_hours ?? 6) * 60;
    const slotMin = parseHM(slot.time);
    if (slotMin == null) return false;
    return (slotMin - nowMinutesUTC()) <= lead;
  };

  const toggleChannelCollapse = (channelId) => {
    setCollapsedChannels(prev => ({ ...prev, [channelId]: !prev[channelId] }));
  };

  const handleCardClick = (cardKey) => {
    if (activeCard === cardKey) {
      setActiveCard('');
    } else {
      setActiveCard(cardKey);
      setStatusFilter('');
      setSelectedDate('');
      setDateFrom('');
      setDateTo('');
    }
  };

  const CARD_LABELS = {
    total_scheduled: 'Total Scheduled',
    today_scheduled: "Today's Events",
    seo_pending: 'SEO Pending',
    sharing_overdue: 'Sharing Overdue',
    without_ppt: 'Without PPT',
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) return toast.error('Title is required');
    setSubmitting(true);
    try {
      const res = await api.createSpecialEvent(formData);
      const eventId = res.data.id;

      if (thumbnailFile) {
        const fd = new FormData();
        fd.append('file', thumbnailFile);
        await api.uploadSpecialEventThumbnail(eventId, fd);
      }
      if (pptFile) {
        const fd = new FormData();
        fd.append('file', pptFile);
        await api.uploadSpecialEventPPT(eventId, fd);
      }

      toast.success('Event created');
      setShowCreateModal(false);
      resetForm();
      fetchEvents();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForReview = null; // Removed - no review workflow

  const handleApprove = null; // Removed

  const handleReject = null; // Removed

  const handleComplete = async (eventId) => {
    try {
      await api.completeSpecialEvent(eventId);
      toast.success('Event marked as done');
      fetchEvents();
      fetchDashboard();
      setShowDetailModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleReopen = async (eventId) => {
    try {
      await api.reopenSpecialEvent(eventId);
      toast.success('Event reopened');
      fetchEvents();
      fetchDashboard();
      setShowDetailModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await api.deleteSpecialEvent(eventId);
      toast.success('Event deleted');
      fetchEvents();
      fetchDashboard();
      setShowDetailModal(false);
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', youtube_link: '', youtube_channel_id: '', event_date: '', event_time: '', teacher_id: '' });
    setThumbnailFile(null);
    setPptFile(null);
  };

  const openDetail = (event) => {
    setSelectedEvent(event);
    setShowDetailModal(true);
  };

  return (
    <div className="space-y-6" data-testid="special-events-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isSchedulingMode ? 'Event Scheduling' : 'Event Library'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isSchedulingMode
              ? `Pin events to YouTube channel notification slots · ${total} events total`
              : `Customise thumbnails, PPTs, SEO & sharing status · ${total} events total`}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          data-testid="create-event-btn"
          className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition"
        >
          <Plus size={18} /> New Event
        </button>
      </div>

      {/* Dashboard Stats — Library mode only */}
      {isLibraryMode && dashboardStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="events-dashboard">
          <div
            onClick={() => handleCardClick('total_scheduled')}
            className={`rounded-xl border p-4 cursor-pointer transition ${activeCard === 'total_scheduled' ? 'bg-indigo-50 border-indigo-400 ring-2 ring-indigo-300' : 'bg-white border-slate-200 hover:shadow-sm'}`}
            data-testid="card-total-scheduled"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${activeCard === 'total_scheduled' ? 'bg-indigo-100' : 'bg-indigo-50'}`}>
                <BarChart3 size={18} className="text-indigo-600" />
              </div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Total Scheduled</span>
            </div>
            <p className={`text-3xl font-bold ${activeCard === 'total_scheduled' ? 'text-indigo-700' : 'text-slate-900'}`} data-testid="stat-total-scheduled">{dashboardStats.total_scheduled}</p>
            <p className="text-xs text-slate-500 mt-1">Draft events pending</p>
          </div>

          <div
            onClick={() => handleCardClick('today_scheduled')}
            className={`rounded-xl border p-4 cursor-pointer transition ${activeCard === 'today_scheduled' ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-300' : 'bg-white border-slate-200 hover:shadow-sm'}`}
            data-testid="card-today-scheduled"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${activeCard === 'today_scheduled' ? 'bg-blue-100' : 'bg-blue-50'}`}>
                <CalendarCheck size={18} className="text-blue-600" />
              </div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Today's Events</span>
            </div>
            <p className={`text-3xl font-bold ${activeCard === 'today_scheduled' ? 'text-blue-700' : 'text-slate-900'}`} data-testid="stat-today-scheduled">{dashboardStats.today_scheduled}</p>
            <p className="text-xs text-slate-500 mt-1">Scheduled for today</p>
          </div>

          <div
            onClick={() => handleCardClick('seo_pending')}
            className={`rounded-xl border p-4 cursor-pointer transition ${activeCard === 'seo_pending' ? 'bg-amber-100 border-amber-400 ring-2 ring-amber-300' : dashboardStats.seo_pending > 0 ? 'bg-amber-50 border-amber-200 hover:shadow-sm' : 'bg-white border-slate-200 hover:shadow-sm'}`}
            data-testid="card-seo-pending"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${activeCard === 'seo_pending' || dashboardStats.seo_pending > 0 ? 'bg-amber-100' : 'bg-slate-50'}`}>
                <SearchX size={18} className={activeCard === 'seo_pending' || dashboardStats.seo_pending > 0 ? 'text-amber-600' : 'text-slate-400'} />
              </div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">SEO Pending</span>
            </div>
            <p className={`text-3xl font-bold ${activeCard === 'seo_pending' || dashboardStats.seo_pending > 0 ? 'text-amber-600' : 'text-slate-900'}`} data-testid="stat-seo-pending">{dashboardStats.seo_pending}</p>
            <p className="text-xs text-slate-500 mt-1">SEO not done yet</p>
          </div>

          <div
            onClick={() => handleCardClick('sharing_overdue')}
            className={`rounded-xl border p-4 cursor-pointer transition ${activeCard === 'sharing_overdue' ? 'bg-red-100 border-red-400 ring-2 ring-red-300' : dashboardStats.sharing_overdue > 0 ? 'bg-red-50 border-red-200 hover:shadow-sm' : 'bg-white border-slate-200 hover:shadow-sm'}`}
            data-testid="card-sharing-overdue"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${activeCard === 'sharing_overdue' || dashboardStats.sharing_overdue > 0 ? 'bg-red-100' : 'bg-slate-50'}`}>
                <AlertCircle size={18} className={activeCard === 'sharing_overdue' || dashboardStats.sharing_overdue > 0 ? 'text-red-600' : 'text-slate-400'} />
              </div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Sharing Overdue</span>
            </div>
            <p className={`text-3xl font-bold ${activeCard === 'sharing_overdue' || dashboardStats.sharing_overdue > 0 ? 'text-red-600' : 'text-slate-900'}`} data-testid="stat-sharing-overdue">{dashboardStats.sharing_overdue}</p>
            <p className="text-xs text-slate-500 mt-1">Time passed, not shared</p>
          </div>

          <div
            onClick={() => handleCardClick('without_ppt')}
            className={`rounded-xl border p-4 cursor-pointer transition ${activeCard === 'without_ppt' ? 'bg-orange-100 border-orange-400 ring-2 ring-orange-300' : dashboardStats.without_ppt > 0 ? 'bg-orange-50 border-orange-200 hover:shadow-sm' : 'bg-white border-slate-200 hover:shadow-sm'}`}
            data-testid="card-without-ppt"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${activeCard === 'without_ppt' || dashboardStats.without_ppt > 0 ? 'bg-orange-100' : 'bg-slate-50'}`}>
                <FileX size={18} className={activeCard === 'without_ppt' || dashboardStats.without_ppt > 0 ? 'text-orange-600' : 'text-slate-400'} />
              </div>
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Without PPT</span>
            </div>
            <p className={`text-3xl font-bold ${activeCard === 'without_ppt' || dashboardStats.without_ppt > 0 ? 'text-orange-600' : 'text-slate-900'}`} data-testid="stat-without-ppt">{dashboardStats.without_ppt}</p>
            <p className="text-xs text-slate-500 mt-1">No PPT uploaded</p>
          </div>
        </div>
      )}

      {/* Active Filter Banner — Library mode only */}
      {isLibraryMode && activeCard && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-100 rounded-lg" data-testid="active-filter-banner">
          <span className="text-sm text-slate-600">Showing: <strong>{CARD_LABELS[activeCard]}</strong></span>
          <button
            onClick={() => setActiveCard('')}
            className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded transition"
            data-testid="clear-card-filter"
          >
            <X size={14} /> Clear filter
          </button>
        </div>
      )}

      {/* Date Filter */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Single Date */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const d = new Date(selectedDate || new Date());
                  d.setDate(d.getDate() - 1);
                  const val = d.toISOString().split('T')[0];
                  setSelectedDate(val);
                  setDateFrom('');
                  setDateTo('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition"
                data-testid="prev-day-btn"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex items-center gap-2">
                <CalendarDays className="text-rose-600" size={20} />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={e => {
                    setSelectedDate(e.target.value);
                    setDateFrom('');
                    setDateTo('');
                    setActiveCard('');
                  }}
                  className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 font-medium"
                  data-testid="date-picker"
                />
              </div>
              <button
                onClick={() => {
                  const d = new Date(selectedDate || new Date());
                  d.setDate(d.getDate() + 1);
                  const val = d.toISOString().split('T')[0];
                  setSelectedDate(val);
                  setDateFrom('');
                  setDateTo('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition"
                data-testid="next-day-btn"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <span className="text-slate-300">|</span>

            {/* Date Range */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500 font-medium">Range:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={e => {
                  setDateFrom(e.target.value);
                  setSelectedDate('');
                }}
                className="px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                data-testid="date-from"
              />
              <span className="text-slate-400">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => {
                  setDateTo(e.target.value);
                  setSelectedDate('');
                }}
                className="px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                data-testid="date-to"
              />
            </div>

            <button
              onClick={() => {
                setSelectedDate('');
                setDateFrom('');
                setDateTo('');
              }}
              className="px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 rounded-lg transition"
              data-testid="clear-date-btn"
            >
              Clear
            </button>
            <button
              onClick={() => {
                setSelectedDate(new Date().toISOString().split('T')[0]);
                setDateFrom('');
                setDateTo('');
              }}
              className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg transition"
              data-testid="today-btn"
            >
              Today
            </button>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-slate-900">
              {selectedDate
                ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                : dateFrom || dateTo
                  ? `${dateFrom || '...'} — ${dateTo || '...'}`
                  : 'All dates'}
            </p>
            <p className="text-xs text-slate-500">{total} event{total !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Status Filters */}
      <div className="flex gap-2 flex-wrap">
        {['', 'draft', 'completed'].map(s => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setActiveCard(''); }}
            data-testid={`filter-${s || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
              statusFilter === s
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {s ? STATUS_LABELS[s] : 'All'}
          </button>
        ))}
      </div>

      {/* Notification slot date picker — Scheduling mode only */}
      {isSchedulingMode && (
      <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid="slot-date-picker-row">
        <Bell size={16} className="text-indigo-600" />
        <span className="text-sm font-medium text-slate-700">Notification slots for</span>
        <input
          type="date"
          value={slotDate}
          onChange={(e) => setSlotDate(e.target.value)}
          className="px-2 py-1 text-sm border border-slate-200 rounded"
          data-testid="slot-date-picker"
        />
        <span className="text-xs text-slate-400">
          Each channel has up to 3 daily slots. Red = deadline missed.
        </span>
      </div>
      )}

      {/* Events grouped by Channel — Scheduling mode only */}
      {isSchedulingMode && (loading ? (
        <div className="text-center py-20 text-slate-400">Loading events...</div>
      ) : groupedEvents.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <Calendar size={48} className="mx-auto mb-3 opacity-50" />
          <p>No channels or events found</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="channel-accordion">
          {groupedEvents.map(group => {
            const ch = channelById.get(group.channelId);
            const collapsed = !!collapsedChannels[group.channelId];
            const lateCount = group.events.filter(isEventLate).length;
            const slots = slotsFor(group.channelId);
            const slotCount = ch?.notification_slots?.length || 0;
            const emptySlotWarn = ch ? (ch.notification_slots || []).filter((_, i) => {
              const assigned = slots.find(a => a.slot_index === i);
              return !assigned && isSlotLate(ch, i, slotDate);
            }).length : 0;

            return (
              <div
                key={group.channelId}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden"
                data-testid={`channel-section-${group.channelId}`}
              >
                {/* Channel Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <button
                    onClick={() => toggleChannelCollapse(group.channelId)}
                    className="flex items-center gap-2 flex-1 text-left"
                    data-testid={`channel-toggle-${group.channelId}`}
                  >
                    <ChevronDown size={18} className={`text-slate-500 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                    <Youtube size={16} className="text-red-500" />
                    <span className="font-semibold text-slate-800">{group.channelName}</span>
                    <span className="text-xs text-slate-500 ml-2">{group.events.length} event{group.events.length === 1 ? '' : 's'}</span>
                    {lateCount > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold" data-testid={`channel-late-${group.channelId}`}>
                        {lateCount} late
                      </span>
                    )}
                    {emptySlotWarn > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-semibold" data-testid={`channel-slot-warn-${group.channelId}`}>
                        {emptySlotWarn} slot{emptySlotWarn === 1 ? '' : 's'} pending
                      </span>
                    )}
                  </button>
                  {ch && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Open create-modal pre-filled with channel + current slot date
                        setFormData(prev => ({
                          ...prev,
                          youtube_channel_id: ch.id,
                          event_date: slotDate || prev.event_date,
                        }));
                        setShowCreateModal(true);
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 rounded transition"
                      data-testid={`channel-add-event-${group.channelId}`}
                      title={`Schedule an event on ${group.channelName} outside notification slots`}
                    >
                      <Plus size={13} /> Add event
                    </button>
                  )}
                  {isManager && ch && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfigChannel(ch); }}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:bg-slate-200 rounded transition"
                      data-testid={`channel-config-${group.channelId}`}
                      title="Configure notification slots + lead time"
                    >
                      <Settings size={13} /> Config
                    </button>
                  )}
                </div>

                {!collapsed && (
                  <>
                    {/* Notification Slot Strip */}
                    {ch && (
                      <div className="border-b border-slate-100 bg-slate-50/40 p-3">
                        {slotCount === 0 ? (
                          <p className="text-xs text-slate-400 italic">
                            No notification slots configured{isManager && <> — click <span className="font-semibold">Config</span> to add up to 3 daily slots.</>}
                          </p>
                        ) : (
                          <div className="flex gap-2 flex-wrap" data-testid={`slots-${group.channelId}`}>
                            {(ch.notification_slots || []).map((slot, idx) => {
                              const assigned = slots.find(a => a.slot_index === idx);
                              const late = !assigned && isSlotLate(ch, idx, slotDate);
                              return (
                                <div
                                  key={idx}
                                  className={`flex-1 min-w-[180px] border rounded-lg p-2.5 ${late ? 'bg-red-50 border-red-300' : assigned ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}
                                  data-testid={`slot-${group.channelId}-${idx}`}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="text-xs font-bold text-slate-500 flex items-center gap-1">
                                      <Clock size={11} /> {slot.time}
                                      {slot.label && <span className="ml-1 font-normal text-slate-400">· {slot.label}</span>}
                                    </div>
                                    {assigned && (
                                      <button
                                        onClick={async () => {
                                          try {
                                            await api.unassignSlot(group.channelId, slotDate, idx);
                                            toast.success('Slot cleared');
                                            fetchSlotAssignments();
                                          } catch (e) { toast.error('Failed'); }
                                        }}
                                        className="text-slate-400 hover:text-red-600 p-0.5"
                                        title="Unassign"
                                        data-testid={`slot-unassign-${group.channelId}-${idx}`}
                                      >
                                        <PinOff size={12} />
                                      </button>
                                    )}
                                  </div>
                                  {assigned ? (
                                    <div className="text-xs">
                                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase mr-1 ${
                                        assigned.item_type === 'special_event'
                                          ? 'bg-purple-100 text-purple-700'
                                          : assigned.item_type === 'regular_class'
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : 'bg-blue-100 text-blue-700'
                                      }`}>
                                        {assigned.item_type === 'special_event'
                                          ? 'Event'
                                          : assigned.item_type === 'regular_class'
                                            ? 'Class'
                                            : 'Class'}
                                      </span>
                                      <span className="text-slate-700 truncate">{assigned.item_title || '—'}</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-between">
                                      <span className={`text-xs ${late ? 'text-red-600 font-semibold' : 'text-slate-400 italic'}`}>
                                        {late ? 'Deadline missed' : 'Unassigned'}
                                      </span>
                                      <button
                                        onClick={() => setAssignSlotContext({ channel: ch, slotIndex: idx })}
                                        className="text-xs px-2 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                        data-testid={`slot-assign-${group.channelId}-${idx}`}
                                      >
                                        Pin event
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Event List (rows) */}
                    {group.events.length === 0 ? (
                      <p className="text-sm text-slate-400 italic px-4 py-6 text-center">No events for this channel in the current filter.</p>
                    ) : (
                      <ul className="divide-y divide-slate-100" data-testid={`events-list-${group.channelId}`}>
                        {group.events.map(ev => {
                          const late = isEventLate(ev);
                          const isOpen = expandedEventId === ev.id;
                          const isUnscheduled = !ev.event_date;
                          return (
                            <li key={ev.id} className="hover:bg-slate-50 transition" data-testid={`event-row-${ev.id}`}>
                              <div
                                className="flex items-center gap-3 px-4 py-2.5 cursor-pointer"
                                onClick={() => setExpandedEventId(isOpen ? null : ev.id)}
                              >
                                <ChevronRight size={14} className={`text-slate-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-90' : ''}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-slate-800 truncate">{ev.title}</span>
                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[ev.status]}`}>
                                      {STATUS_LABELS[ev.status]}
                                    </span>
                                    {isUnscheduled && (
                                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-700" data-testid={`event-unscheduled-${ev.id}`}>
                                        Unscheduled
                                      </span>
                                    )}
                                    {late && (
                                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700" data-testid={`event-late-${ev.id}`}>
                                        Late
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                                    {ev.teacher_name && <span className="flex items-center gap-1"><User size={11} /> {ev.teacher_name}</span>}
                                    {ev.event_date && <span className="flex items-center gap-1"><Calendar size={11} /> {ev.event_date}{ev.event_time ? ` · ${ev.event_time}` : ''}</span>}
                                    {ev.ppt_url && <FileText size={11} className="text-blue-500" title="PPT attached" />}
                                    {ev.thumbnail_url && <Image size={11} className="text-emerald-500" title="Thumbnail attached" />}
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => { e.stopPropagation(); openDetail(ev); }}
                                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
                                  title="Full editor"
                                  data-testid={`event-open-${ev.id}`}
                                >
                                  <Edit2 size={13} />
                                </button>
                              </div>
                              {isOpen && (
                                <div className="px-8 pb-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50/50" data-testid={`event-expanded-${ev.id}`}>
                                  <div className="md:col-span-1">
                                    <div className="text-xs font-semibold text-slate-500 mb-1">Thumbnail</div>
                                    {ev.thumbnail_url ? (
                                      <a href={ev.thumbnail_url} target="_blank" rel="noreferrer">
                                        <img src={ev.thumbnail_url} alt="thumb" className="w-full h-32 object-cover rounded-lg border border-slate-200 hover:border-indigo-400" />
                                      </a>
                                    ) : (
                                      <div className="h-32 flex items-center justify-center bg-slate-100 rounded-lg text-slate-300 text-xs">No thumbnail</div>
                                    )}
                                  </div>
                                  <div className="md:col-span-2 space-y-2 text-sm">
                                    {ev.description && <p className="text-slate-700 whitespace-pre-wrap">{ev.description}</p>}
                                    <div className="flex flex-wrap gap-3 text-xs">
                                      {ev.youtube_link && <a href={ev.youtube_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-red-600 hover:underline"><Youtube size={12} /> YouTube link</a>}
                                      {ev.ppt_url && <a href={ev.ppt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline"><FileText size={12} /> Download PPT</a>}
                                    </div>
                                    <div className="flex gap-3 text-xs">
                                      <span className={ev.seo_done ? 'text-green-600' : 'text-slate-400'}>{ev.seo_done ? '✓' : '○'} SEO</span>
                                      <span className={ev.telegram_shared ? 'text-green-600' : 'text-slate-400'}>{ev.telegram_shared ? '✓' : '○'} Telegram</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Library flat card grid — Library mode only */}
      {isLibraryMode && (
        loading ? (
          <div className="text-center py-20 text-slate-400">Loading events...</div>
        ) : events.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <Calendar size={48} className="mx-auto mb-3 opacity-50" />
            <p>No events found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-events-grid">
            {events.map(event => (
              <div
                key={event.id}
                onClick={() => openDetail(event)}
                data-testid={`event-card-${event.id}`}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition cursor-pointer"
              >
                <div className="h-36 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  {event.thumbnail_url ? (
                    <img src={event.thumbnail_url} alt={event.title} className="w-full h-full object-cover" />
                  ) : (
                    <Image size={40} className="text-slate-300" />
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-slate-900 line-clamp-1">{event.title}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_COLORS[event.status]}`}>
                      {STATUS_LABELS[event.status]}
                    </span>
                  </div>
                  {event.teacher_name && (
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <User size={14} /> {event.teacher_name}
                    </p>
                  )}
                  {event.youtube_channel_name && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <Youtube size={14} /> {event.youtube_channel_name}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                    {event.event_date ? (
                      <span className="flex items-center gap-1"><Calendar size={12} /> {event.event_date}{event.event_time ? ` at ${event.event_time}` : ''}</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-700">Unscheduled</span>
                    )}
                    {event.youtube_link && <Youtube size={14} className="text-red-500" />}
                    {event.ppt_url && <FileText size={14} className="text-blue-500" />}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <span className={`flex items-center gap-1 ${event.seo_done ? 'text-green-600' : 'text-slate-400'}`}>
                      <Check size={12} /> SEO
                    </span>
                    <span className={`flex items-center gap-1 ${event.telegram_shared ? 'text-green-600' : 'text-slate-400'}`}>
                      <Check size={12} /> Telegram
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-event-modal">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-lg font-bold">Create Special Event</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Title *</label>
                <input
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="event-title-input"
                  placeholder="Event title"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Event description"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">YouTube Link</label>
                <input
                  value={formData.youtube_link}
                  onChange={e => setFormData({ ...formData, youtube_link: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="event-youtube-input"
                  placeholder="https://youtube.com/..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">YouTube Channel</label>
                <select
                  value={formData.youtube_channel_id}
                  onChange={e => setFormData({ ...formData, youtube_channel_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="event-channel-select"
                >
                  <option value="">Select Channel</option>
                  {channels.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Event Date</label>
                <input
                  type="date"
                  value={formData.event_date}
                  onChange={e => setFormData({ ...formData, event_date: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Event Time</label>
                <input
                  type="time"
                  value={formData.event_time}
                  onChange={e => setFormData({ ...formData, event_time: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="event-time-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Assign Teacher</label>
                <select
                  value={formData.teacher_id}
                  onChange={e => setFormData({ ...formData, teacher_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="event-teacher-select"
                >
                  <option value="">Select Teacher</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Thumbnail</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => setThumbnailFile(e.target.files[0])}
                  className="w-full mt-1 text-sm"
                  data-testid="event-thumbnail-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">PPT File</label>
                <input
                  type="file"
                  accept=".ppt,.pptx,.pdf"
                  onChange={e => setPptFile(e.target.files[0])}
                  className="w-full mt-1 text-sm"
                  data-testid="event-ppt-input"
                />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button
                onClick={handleCreate}
                disabled={submitting}
                data-testid="create-event-submit"
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50"
              >
                {submitting ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          isManager={isManager}
          teachers={teachers}
          channels={channels}
          onClose={() => setShowDetailModal(false)}
          onComplete={handleComplete}
          onReopen={handleReopen}
          onDelete={handleDelete}
          onRefresh={fetchEvents}
        />
      )}

      {/* Reject Modal - Removed, no review workflow */}

      {/* Notification Config Modal */}
      {configChannel && (
        <NotificationConfigModal
          channel={configChannel}
          onClose={() => setConfigChannel(null)}
          onSaved={(updated) => {
            setConfigChannel(null);
            setChannels(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
            fetchSlotAssignments();
          }}
        />
      )}

      {/* Assign Slot Modal */}
      {assignSlotContext && (
        <AssignSlotModal
          context={assignSlotContext}
          date={slotDate}
          events={events}
          onClose={() => setAssignSlotContext(null)}
          onAssigned={() => {
            setAssignSlotContext(null);
            fetchSlotAssignments();
          }}
        />
      )}
    </div>
  );
};

// ─── Notification Slot Config (per channel) ──────────────────────────────────
// ─── SEO Tags (YouTube Studio–style chip input) ──────────────────────────────
const SeoTagsInput = ({ tags = [], onChange, disabled = false, maxChars = 500, maxTags = 40 }) => {
  const [draft, setDraft] = React.useState('');
  const usedChars = tags.join(', ').length + (tags.length > 0 ? 2 : 0) + draft.length;
  const percent = Math.min(100, Math.round((usedChars / maxChars) * 100));

  const commit = (text) => {
    const parts = text.split(',').map(s => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const seen = new Set(tags.map(t => t.toLowerCase()));
    const next = [...tags];
    for (const p of parts) {
      const key = p.toLowerCase();
      if (seen.has(key)) continue;
      if (next.length >= maxTags) break;
      const truncated = p.slice(0, 100);
      const projected = [...next, truncated].join(', ').length;
      if (projected > maxChars) break;
      next.push(truncated);
      seen.add(key);
    }
    if (next.length !== tags.length || next.some((t, i) => t !== tags[i])) onChange(next);
    setDraft('');
  };

  const removeTag = (idx) => onChange(tags.filter((_, i) => i !== idx));

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (draft.trim()) commit(draft);
    } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (text.includes(',') || text.includes('\n')) {
      e.preventDefault();
      commit(text.replace(/\n/g, ','));
    }
  };

  return (
    <div className="space-y-1.5">
      <div
        className={`flex flex-wrap items-center gap-1.5 px-2 py-2 border rounded-lg min-h-[70px] bg-white ${disabled ? 'bg-slate-50' : 'border-slate-300 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100'}`}
        data-testid="seo-tags-input"
        onClick={() => {
          const el = document.getElementById('seo-tags-draft-input');
          if (el) el.focus();
        }}
      >
        {tags.map((t, i) => (
          <span
            key={`${t}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full border border-indigo-100"
            data-testid={`seo-tag-chip-${i}`}
          >
            {t}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeTag(i); }}
                className="text-indigo-400 hover:text-indigo-700"
                data-testid={`seo-tag-remove-${i}`}
                aria-label={`Remove tag ${t}`}
              >
                <X size={11} />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            id="seo-tags-draft-input"
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            onPaste={handlePaste}
            onBlur={() => draft.trim() && commit(draft)}
            placeholder={tags.length === 0 ? 'Add tags (comma or Enter to separate)…' : ''}
            className="flex-1 min-w-[140px] outline-none text-sm bg-transparent"
            data-testid="seo-tags-draft"
          />
        )}
      </div>
      {!disabled && (
        <div className="flex items-center justify-between text-[11px] text-slate-400">
          <span>Press <kbd className="px-1 bg-slate-100 rounded border border-slate-200">Enter</kbd> or <kbd className="px-1 bg-slate-100 rounded border border-slate-200">,</kbd> after each tag</span>
          <span className={usedChars > maxChars ? 'text-red-500 font-semibold' : ''} data-testid="seo-tags-count">
            {usedChars}/{maxChars} · {tags.length}/{maxTags} tags · {percent}%
          </span>
        </div>
      )}
    </div>
  );
};


const NotificationConfigModal = ({ channel, onClose, onSaved }) => {
  const [slots, setSlots] = useState(
    channel.notification_slots?.length
      ? channel.notification_slots.slice(0, 3)
      : []
  );
  const [leadHours, setLeadHours] = useState(channel.notification_lead_hours ?? 6);
  const [saving, setSaving] = useState(false);

  const addSlot = () => {
    if (slots.length >= 3) return;
    setSlots([...slots, { label: '', time: '09:00' }]);
  };
  const removeSlot = (idx) => setSlots(slots.filter((_, i) => i !== idx));
  const updateSlot = (idx, key, value) => {
    setSlots(slots.map((s, i) => i === idx ? { ...s, [key]: value } : s));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.updateYouTubeChannelNotificationConfig(channel.id, {
        notification_slots: slots.filter(s => s.time),
        notification_lead_hours: Math.max(0, Math.min(48, Number(leadHours) || 6)),
      });
      toast.success('Notification config saved');
      onSaved({ ...channel, notification_slots: res.data.notification_slots, notification_lead_hours: res.data.notification_lead_hours });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="notification-config-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2"><Bell size={16} className="text-indigo-600" /> Notification Slots</h3>
            <p className="text-xs text-slate-500">{channel.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-500">
            Configure up to <b>3 daily notification slots</b>. Each slot needs a class or a special event pinned to it. Warning shows if a slot is empty within the lead time.
          </p>

          <div className="space-y-2" data-testid="slots-editor">
            {slots.length === 0 && (
              <p className="text-sm text-slate-400 italic">No slots yet — click &ldquo;Add slot&rdquo; below.</p>
            )}
            {slots.map((s, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <span className="text-xs font-bold text-slate-500 w-6">#{idx + 1}</span>
                <input
                  type="time"
                  value={s.time || ''}
                  onChange={(e) => updateSlot(idx, 'time', e.target.value)}
                  className="px-2 py-1 text-sm border border-slate-200 rounded"
                  data-testid={`slot-time-${idx}`}
                />
                <input
                  type="text"
                  placeholder="Label (optional) e.g., Morning"
                  value={s.label || ''}
                  onChange={(e) => updateSlot(idx, 'label', e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded"
                  data-testid={`slot-label-${idx}`}
                />
                <button
                  onClick={() => removeSlot(idx)}
                  className="p-1 text-slate-400 hover:text-red-600"
                  data-testid={`slot-remove-${idx}`}
                ><Trash2 size={14} /></button>
              </div>
            ))}
            {slots.length < 3 && (
              <button
                onClick={addSlot}
                className="w-full py-2 border-2 border-dashed border-slate-200 text-sm text-slate-500 hover:border-indigo-400 hover:text-indigo-600 rounded-lg transition"
                data-testid="add-slot-btn"
              >
                <Plus size={13} className="inline mr-1" /> Add slot ({slots.length}/3)
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700">Notification lead time (hours)</label>
            <input
              type="number"
              min="0"
              max="48"
              value={leadHours}
              onChange={(e) => setLeadHours(e.target.value)}
              className="w-20 px-2 py-1 text-sm border border-slate-200 rounded"
              data-testid="lead-hours-input"
            />
            <span className="text-xs text-slate-400">event must be assigned this many hours before slot time</span>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50" data-testid="save-notification-config">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Assign Event to a Notification Slot ─────────────────────────────────────
const AssignSlotModal = ({ context, date, events, onClose, onAssigned }) => {
  const { channel, slotIndex } = context;
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [freshEvents, setFreshEvents] = useState(null);

  // Fetch the FULL pool of non-completed events on mount so the picker isn't
  // constrained by whatever filter the user has active on the main scheduling page.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getSpecialEvents('limit=500');
        if (!cancelled) setFreshEvents(res.data.events || []);
      } catch (e) {
        // Log but fall back gracefully — pin modal will use whatever events the parent has
        // eslint-disable-next-line no-console
        console.warn('AssignSlotModal: full-event fetch failed, using parent list', e);
        if (!cancelled) setFreshEvents(events || []);
      }
    })();
    return () => { cancelled = true; };
  }, [events]);

  const pool = freshEvents ?? events ?? [];
  // Show non-completed events. Include those with no channel too, and those
  // already tagged to this channel. Anything on a DIFFERENT channel is hidden.
  const filtered = pool.filter(ev => {
    if (ev.status === 'completed') return false;
    if (ev.youtube_channel_id && ev.youtube_channel_id !== channel.id) return false;
    if (q && !ev.title.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const pick = async (ev) => {
    setBusy(true);
    try {
      await api.assignSlot({
        channel_id: channel.id,
        date,
        slot_index: slotIndex,
        item_type: 'special_event',
        item_id: ev.id,
      });
      toast.success('Pinned to slot');
      onAssigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const pinAsRegularClass = async () => {
    setBusy(true);
    try {
      await api.assignSlot({
        channel_id: channel.id,
        date,
        slot_index: slotIndex,
        item_type: 'regular_class',
        item_id: 'regular_class',  // sentinel, backend ignores lookup
      });
      toast.success('Slot marked for a regular class');
      onAssigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const slot = channel.notification_slots?.[slotIndex];
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="assign-slot-modal">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2"><Link2 size={16} /> Pin an event or class</h3>
            <p className="text-xs text-slate-500">{channel.name} · Slot #{slotIndex + 1} at {slot?.time} · {date}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {/* Regular-class shortcut */}
        <div className="px-5 pt-3">
          <button
            onClick={pinAsRegularClass}
            disabled={busy}
            className="w-full flex items-center justify-between px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-left disabled:opacity-50"
            data-testid="assign-regular-class-btn"
          >
            <div>
              <div className="font-medium text-sm text-emerald-800">Mark as Regular Class</div>
              <div className="text-xs text-emerald-600">Use this slot for a normal class — no special event needed</div>
            </div>
            <span className="text-emerald-700"><Link2 size={16} /></span>
          </button>
        </div>

        <div className="px-5 pt-3 pb-1">
          <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Or pick a special event</div>
          <input
            type="text"
            placeholder="Search events…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded"
            data-testid="assign-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {freshEvents === null ? (
            <p className="text-sm text-slate-400 italic text-center py-8">Loading events…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-400 italic text-center py-8">
              No matching events. Create a new event from &ldquo;New Event&rdquo; and it will appear here.
            </p>
          ) : (
            <ul className="space-y-1" data-testid="assign-candidates">
              {filtered.map(ev => (
                <li key={ev.id}>
                  <button
                    onClick={() => pick(ev)}
                    disabled={busy}
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 rounded-lg disabled:opacity-50 border border-transparent hover:border-indigo-200"
                    data-testid={`assign-pick-${ev.id}`}
                  >
                    <div className="font-medium text-sm text-slate-800 flex items-center gap-2">
                      {ev.title}
                      {!ev.youtube_channel_id && (
                        <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-[10px] font-semibold">No channel</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {ev.event_date ? `${ev.event_date}${ev.event_time ? ' · ' + ev.event_time : ''}` : 'Unscheduled'}
                      {ev.teacher_name && <> · {ev.teacher_name}</>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const EventDetailModal = ({ event, isManager, teachers, channels, onClose, onComplete, onReopen, onDelete, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    title: event.title || '',
    description: event.description || '',
    youtube_link: event.youtube_link || '',
    youtube_channel_id: event.youtube_channel_id || '',
    event_date: event.event_date || '',
    event_time: event.event_time || '',
    teacher_id: event.teacher_id || '',
  });

  const handleUploadThumbnail = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.uploadSpecialEventThumbnail(event.id, fd);
      toast.success('Thumbnail uploaded');
      onRefresh();
      onClose();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadPPT = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.uploadSpecialEventPPT(event.id, fd);
      toast.success('PPT uploaded');
      onRefresh();
      onClose();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editData.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      await api.updateSpecialEvent(event.id, editData);
      toast.success('Event updated');
      setEditing(false);
      onRefresh();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const canEdit = event.status !== 'completed';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="event-detail-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="relative">
          <div className="h-48 bg-gradient-to-br from-slate-200 to-slate-300">
            {event.thumbnail_url && (
              <img src={event.thumbnail_url} alt={event.title} className="w-full h-full object-cover" />
            )}
          </div>
          <button onClick={onClose} className="absolute top-3 right-3 bg-white/80 rounded-full p-1.5 hover:bg-white">
            <X size={18} />
          </button>
          <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[event.status]}`}>
            {STATUS_LABELS[event.status]}
          </span>
        </div>

        <div className="p-6 space-y-4">
          {/* Edit Mode */}
          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Title *</label>
                <input value={editData.title} onChange={e => setEditData({...editData, title: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border rounded-lg" data-testid="edit-title-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Description</label>
                <textarea value={editData.description} onChange={e => setEditData({...editData, description: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border rounded-lg" rows={2} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">YouTube Link</label>
                <input value={editData.youtube_link} onChange={e => setEditData({...editData, youtube_link: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border rounded-lg" placeholder="https://youtube.com/..." />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">YouTube Channel</label>
                <select value={editData.youtube_channel_id} onChange={e => setEditData({...editData, youtube_channel_id: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border rounded-lg" data-testid="edit-channel-select">
                  <option value="">Select Channel</option>
                  {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Event Date</label>
                <input type="date" value={editData.event_date} onChange={e => setEditData({...editData, event_date: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Event Time</label>
                <input type="time" value={editData.event_time || ''} onChange={e => setEditData({...editData, event_time: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Teacher</label>
                <select value={editData.teacher_id} onChange={e => setEditData({...editData, teacher_id: e.target.value})}
                  className="w-full mt-1 px-3 py-2 border rounded-lg" data-testid="edit-teacher-select">
                  <option value="">Select Teacher</option>
                  {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Thumbnail</label>
                  <label className="flex items-center gap-2 mt-1 px-3 py-2 border border-dashed border-slate-300 rounded-lg text-sm cursor-pointer hover:bg-slate-50 transition">
                    <Upload size={16} className="text-slate-400" />
                    <span className="text-slate-500">{event.thumbnail_url ? 'Replace Thumbnail' : 'Upload Thumbnail'}</span>
                    <input type="file" accept="image/*" onChange={handleUploadThumbnail} className="hidden" disabled={uploading} />
                  </label>
                  {event.thumbnail_url && <p className="text-xs text-green-600 mt-1">Current thumbnail set</p>}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">PPT File</label>
                  <label className="flex items-center gap-2 mt-1 px-3 py-2 border border-dashed border-slate-300 rounded-lg text-sm cursor-pointer hover:bg-slate-50 transition">
                    <Upload size={16} className="text-slate-400" />
                    <span className="text-slate-500">{event.ppt_url ? 'Replace PPT' : 'Upload PPT'}</span>
                    <input type="file" accept=".ppt,.pptx,.pdf" onChange={handleUploadPPT} className="hidden" disabled={uploading} />
                  </label>
                  {event.ppt_url && <p className="text-xs text-green-600 mt-1">Current PPT set</p>}
                </div>
              </div>
              {uploading && <p className="text-sm text-amber-600">Uploading file...</p>}
              <div className="flex gap-3 pt-2">
                <button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn"
                  className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => setEditing(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              {/* View Mode */}
              <div className="flex items-start justify-between">
                <h2 className="text-xl font-bold text-slate-900">{event.title}</h2>
                {canEdit && (
                  <button onClick={() => setEditing(true)} data-testid="edit-event-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg">
                    <Edit2 size={14} /> Edit
                  </button>
                )}
              </div>
              {event.description && <p className="text-slate-600">{event.description}</p>}

              {event.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  <strong>Rejected:</strong> {event.rejection_reason}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Teacher</span>
                  <p className="font-medium">{event.teacher_name || 'Not assigned'}</p>
                </div>
                <div>
                  <span className="text-slate-400">YouTube Channel</span>
                  <p className="font-medium">{event.youtube_channel_name || 'Not selected'}</p>
                </div>
                <div>
                  <span className="text-slate-400">Event Date</span>
                  <p className="font-medium">{event.event_date || 'Not set'}{event.event_time ? ` at ${event.event_time}` : ''}</p>
                </div>
                <div>
                  <span className="text-slate-400">Created By</span>
                  <p className="font-medium">{event.created_by_name}</p>
                </div>
                <div>
                  <span className="text-slate-400">Created</span>
                  <p className="font-medium">{new Date(event.created_at).toLocaleDateString()}</p>
                </div>
                {event.reviewed_by_name && (
                  <div>
                    <span className="text-slate-400">Reviewed By</span>
                    <p className="font-medium">{event.reviewed_by_name}</p>
                  </div>
                )}
              </div>

              {/* Links & Files */}
              <div className="flex flex-wrap gap-3">
                {event.youtube_link && (
                  <a href={event.youtube_link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm hover:bg-red-100"
                    data-testid="youtube-link">
                    <Youtube size={16} /> YouTube Link <ExternalLink size={14} />
                  </a>
                )}
                {event.ppt_url && (
                  <a href={event.ppt_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100"
                    data-testid="ppt-link">
                    <FileText size={16} /> Download PPT <ExternalLink size={14} />
                  </a>
                )}
              </div>

              {/* Upload buttons for draft/rejected */}
              {canEdit && (
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm cursor-pointer hover:bg-slate-50">
                    <Upload size={16} /> {event.thumbnail_url ? 'Replace Thumbnail' : 'Upload Thumbnail'}
                    <input type="file" accept="image/*" onChange={handleUploadThumbnail} className="hidden" disabled={uploading} />
                  </label>
                  <label className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm cursor-pointer hover:bg-slate-50">
                    <Upload size={16} /> {event.ppt_url ? 'Replace PPT' : 'Upload PPT'}
                    <input type="file" accept=".ppt,.pptx,.pdf" onChange={handleUploadPPT} className="hidden" disabled={uploading} />
                  </label>
                </div>
              )}

              {/* SEO Tags — YouTube-Studio-like chip input for search-tag SEO */}
              <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2" data-testid="seo-tags-section">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                    <Search size={14} className="text-indigo-500" /> SEO Tags
                  </h4>
                  <div className="flex items-center gap-2">
                    {(event.seo_tags && event.seo_tags.length > 0) && (
                      <>
                        <span className="text-xs text-slate-400">{event.seo_tags.length} tag{event.seo_tags.length === 1 ? '' : 's'}</span>
                        <button
                          type="button"
                          onClick={async () => {
                            const text = (event.seo_tags || []).join(', ');
                            try {
                              if (navigator.clipboard?.writeText) {
                                await navigator.clipboard.writeText(text);
                              } else {
                                // Fallback for older browsers
                                const ta = document.createElement('textarea');
                                ta.value = text;
                                ta.style.position = 'fixed';
                                ta.style.opacity = '0';
                                document.body.appendChild(ta);
                                ta.select();
                                document.execCommand('copy');
                                document.body.removeChild(ta);
                              }
                              toast.success(`Copied ${event.seo_tags.length} tag${event.seo_tags.length === 1 ? '' : 's'} — paste into YouTube Studio`);
                            } catch (err) {
                              toast.error('Copy failed — please copy manually');
                            }
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded transition"
                          data-testid="seo-tags-copy-btn"
                          title="Copy tags (comma-separated) to clipboard"
                        >
                          <Copy size={12} /> Copy
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-400">
                  Keywords for YouTube SEO. Separate with <kbd className="px-1 bg-slate-100 rounded">Enter</kbd> or comma. Kept on this event so the SEO team can copy them into YouTube Studio.
                </p>
                <SeoTagsInput
                  tags={event.seo_tags || []}
                  disabled={!canEdit}
                  onChange={async (nextTags) => {
                    try {
                      await api.updateSpecialEvent(event.id, { seo_tags: nextTags });
                      onRefresh();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update tags'); }
                  }}
                />
              </div>

              {/* Checkpoints */}
              {canEdit && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700">Checkpoints</h4>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="seo-checkbox">
                    <input
                      type="checkbox"
                      checked={event.seo_done || false}
                      onChange={async () => {
                        try {
                          await api.updateSpecialEvent(event.id, { seo_done: !event.seo_done });
                          onRefresh();
                        } catch (e) { toast.error('Failed to update'); }
                      }}
                      className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                    />
                    <span className={`text-sm ${event.seo_done ? 'text-green-700 font-medium' : 'text-slate-600'}`}>SEO Done</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="telegram-checkbox">
                    <input
                      type="checkbox"
                      checked={event.telegram_shared || false}
                      onChange={async () => {
                        try {
                          await api.updateSpecialEvent(event.id, { telegram_shared: !event.telegram_shared });
                          onRefresh();
                        } catch (e) { toast.error('Failed to update'); }
                      }}
                      className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                    />
                    <span className={`text-sm ${event.telegram_shared ? 'text-green-700 font-medium' : 'text-slate-600'}`}>Telegram Sharing Done</span>
                  </label>
                </div>
              )}
              {!canEdit && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-slate-700">Checkpoints</h4>
                  <p className={`text-sm flex items-center gap-2 ${event.seo_done ? 'text-green-600' : 'text-red-500'}`}>
                    <Check size={14} /> SEO {event.seo_done ? 'Done' : 'Not Done'}
                  </p>
                  <p className={`text-sm flex items-center gap-2 ${event.telegram_shared ? 'text-green-600' : 'text-red-500'}`}>
                    <Check size={14} /> Telegram {event.telegram_shared ? 'Shared' : 'Not Shared'}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                {event.status === 'draft' && (
                  <button onClick={() => onComplete(event.id)} data-testid="complete-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                    <Check size={16} /> Mark as Done
                  </button>
                )}
                {event.status === 'completed' && (
                  <button onClick={() => onReopen(event.id)} data-testid="reopen-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700">
                    <RotateCcw size={16} /> Reopen
                  </button>
                )}
                <button onClick={() => onDelete(event.id)} data-testid="delete-event-btn"
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg ml-auto">
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpecialEvents;
